import { useEffect, useRef, useState } from "react";
import { Upload, CreditCard, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import CardTransactionDrawer from "@/components/CardTransactionDrawer";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_IMPORT_ROLES } from "@/constants/roles";
import { fmtDate } from "@/lib/utils";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";
import useCardSync from "@/hooks/useCardSync";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const FILTERS = [
  { value: "unmatched", label: "Unmatched" },
  { value: "coded", label: "Coded" },
  { value: "matched", label: "Matched" },
  { value: "excluded", label: "Excluded" },
  { value: "", label: "All" },
];

export default function CardTransactions() {
  const [status, setStatus] = useState("unmatched");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobs, setJobs] = useState([]);
  const q = useDebounced(search);
  // Search + filters run on the server across every imported transaction.
  // Admins and accounting see every charge; anyone else only their own.
  const list = usePagedSearch("/contractor/card-transactions/search", { q, status, date_from: dateFrom, date_to: dateTo, job_id: jobId });
  const txns = list.items;
  const counts = list.counts || {};
  const load = list.reload;
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const canManage = CARD_IMPORT_ROLES.includes(user?.role);
  const [rampConnected, setRampConnected] = useState(false);
  // V1: Ramp posts charges to QuickBooks, so they're coded on this page.
  const [rampPosts, setRampPosts] = useState(false);

  useEffect(() => {
    api.get("/contractor/integrations").then((r) => {
      const row = r.data.find((i) => i.type === "ramp" && i.status === "connected");
      setRampConnected(!!row);
      setRampPosts(row?.qbo_poster === "ramp");
    }).catch(() => {});
    api.get("/contractor/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  // Pulls new charges (and their receipts) straight from Ramp's API — in
  // the background, since a first sync can take minutes.
  const { syncing, start: syncRamp } = useCardSync("ramp", () => load(), rampConnected);


  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contractor/card-transactions/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} (${data.matched} matched automatically), ${data.duplicates} duplicate${data.duplicates === 1 ? "" : "s"}, ${data.failed} failed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import CSV");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!txns) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="card-transactions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
            <CreditCard size={22} className="text-sky-500" /> Ramp Transactions
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            {rampPosts
              ? "Ramp card charges (synced from Ramp daily). Code each one here — job, QuickBooks category, memo and receipt — then mark it ready and Ramp posts it to QuickBooks. No Expense Log entry needed."
              : "Ramp card charges (synced from Ramp daily, or uploaded as a Ramp export), each checked against the Expense Log entry (or purchase order) paid with Ramp — its Ramp receipt comes along. Matching is a check only — the entry or PO is what goes to job cost and Accounting."}
            {!canManage && " You see only your own charges."}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {rampConnected && user?.role === "company_admin" && (
              <button onClick={syncRamp} disabled={syncing} data-testid="sync-ramp-button"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold disabled:opacity-60">
                <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync from Ramp"}
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-button"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60"
            >
              <Upload size={14} /> {uploading ? "Importing…" : "Upload Ramp CSV"}
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={uploadCsv} data-testid="csv-file-input" className="hidden" />
          </div>
        )}
      </div>

      <ListSearchBar value={search} onChange={setSearch} placeholder="Search merchant, cardholder, last 4, transaction id…" testId="card-txn-search">
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <select value={jobId} onChange={(e) => setJobId(e.target.value)} title="Job" data-testid="card-txn-job-filter" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px] max-w-[220px]">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
        </select>
      </ListSearchBar>

      <div className="flex gap-2 mt-4 border-b border-slate-100">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"} onClick={() => setStatus(f.value)} data-testid={`filter-${f.value || "all"}`}
            className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${status === f.value ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {f.label}{counts[f.value || "all"] ? ` (${counts[f.value || "all"].toLocaleString()})` : ""}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-[13px]" data-testid="card-transactions-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2">Merchant</th>
              <th className="text-left py-2">Date</th>
              <th className="text-left py-2">Cardholder</th>
              <th className="text-left py-2">Job</th>
              <th className="text-left py-2">Expense / PO</th>
              <th className="text-right py-2">Amount</th>
              <th className="text-left py-2 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.id} onClick={() => setSelected(t.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`card-txn-row-${t.id}`}>
                <td className="py-2.5 border-t border-slate-100 font-medium">{t.merchant}</td>
                <td className="py-2.5 border-t border-slate-100 text-slate-500">{fmtDate(t.txn_date)}</td>
                <td className="py-2.5 border-t border-slate-100 text-slate-500">{t.cardholder || "—"}</td>
                <td className="py-2.5 border-t border-slate-100 text-[12.5px]" data-testid={`card-txn-job-${t.id}`}>
                  {t.job ? <span title={t.job.name}>{t.job.code}</span> : t.purchasing_context ? t.purchasing_context.name : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2.5 border-t border-slate-100 mono text-[12.5px]">{(t.matched_expense || t.matched_po) ? (t.matched_expense || t.matched_po).number : <span className="text-slate-300">—</span>}</td>
                <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(t.amount)}</td>
                <td className="py-2.5 border-t border-slate-100 pl-3">
                  <StatusBadge status={t.status} />
                  {t.missing_info && <span className="ml-1.5 ss-pill ss-pill-amber" title="Info requested from the cardholder" data-testid={`needs-info-${t.id}`}>Needs info</span>}
                  {t.ramp_ready_at && <span className={`ml-1.5 ss-pill ${t.ramp_sync_status === "SYNCED" ? "ss-pill-green" : "ss-pill-sky"}`}>{t.ramp_sync_status === "SYNCED" ? "In QuickBooks" : "Ready"}</span>}
                  {t.double_posted && <span className="ml-1.5 ss-pill ss-pill-red" title="Ramp also posted this charge to QuickBooks">In QuickBooks twice</span>}
                </td>
              </tr>
            ))}
            {txns.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400">{q ? "No Ramp charges match your search." : status === "unmatched" ? (rampPosts ? "Nothing waiting to be coded." : "Nothing to match — upload a Ramp transactions export to check its charges.") : `No ${status || ""} Ramp charges.`}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <LoadMore items={txns} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore} total={counts[status || "all"]} />

      <CardTransactionDrawer txnId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onChanged={load} canManage={canManage} rampPosts={rampPosts} />
    </div>
  );
}
